import { ApplicationInterface } from "./application-interface";
import { ColumnDef } from "./column-definition";

// TODO: add account ID to constructor and copyOf

export class Application {
    /** Attribute fields */
    private _id: number;
    private _applicationID: number;
    private _firstName: string;
    private _lastName: string;
    private _businessName: string;
    private _phoneNumber: string;
    private _approvedAmount: number;
    private _status: string;
    private _supplierName: string;

    private _accountID: string = "";
    private _salesRep: string = "";
    private _appliedOn: string = "";
    private _requestedAmount: number = 0;
    private _applicationGuid: string = "";
    private _borrowerApr: string = "";

    /** Validators for applications */
    //public static validators = validators;

    /**
     * Construct the application from an interface
     */
    constructor(intf: ApplicationInterface) {
        /*
        this._id = intf.id;
        this._applicationID = intf.applicationID || "";
        this._name = (intf.firstName || "") + " " + (intf.lastName || "");
        this._businessName = intf.businessName || "";
        this._phoneNumber = intf.phoneNumber || "";
        this._appliedOn = intf.appliedOn || "";
        this._approvedAmount = intf.approvedAmount;
        this._status = intf.status || "";
        this._supplierName = intf.supplierName || "";
        this._requestedAmount = intf.requestedAmount;
        */
        this._id = intf.id;
        this._applicationID = intf.applicationid || 0;
        this._firstName = intf.firstName?.trim() || "";
        this._lastName = intf.lastName?.trim() || "";
        this._businessName = intf.companyName?.trim() || "";
        this._phoneNumber = intf.phone?.trim() || "";
        this._approvedAmount = intf.amount || 0;
        this._status = intf.approval?.trim() || "";
        this._supplierName = intf.supplierName?.trim() || "";
        this._applicationGuid = intf.applicationGuid?.trim() || "";
        this._borrowerApr = intf.borrowerApr?.trim() || "";
    }

    /**
     * Determine if two applications are equal
     * 
     * @param app The other application to check
     * @returns True if applications are equal
     */
    public equals(app: Application): boolean {
        return this.id === app.id &&
            this.accountID === app.accountID &&
            this.firstName === app.firstName &&
            this.lastName === app.lastName &&
            this.businessName === app.businessName &&
            this.appliedOn === app.appliedOn &&
            this.phoneNumber === app.phoneNumber &&
            this.salesRep === app.salesRep &&
            this.approvedAmount === app.approvedAmount &&
            this.requestedAmount === app.requestedAmount &&
            this.status === app.status &&
            this.applicationGuid === app.applicationGuid &&
            this.borrowerApr === app.borrowerApr;
    }

    /**
     * Return a copy of this application
     * 
     * NOTE: We make a semi-dangerous assumption here to split the name in two.
     * This could cause problems but because applications should not be edited
     * in this state, I'm going to leave this as is. A better method would be either
     * storing the full name on the backend or displaying the first/last name on a
     * frontend dialog and changing this model.
     */
    public copyOf(): Application {
        const intf: ApplicationInterface = {
            id: this.id,
            applicationid: this.applicationID,
            firstName: this._firstName,
            lastName: this._lastName,
            companyName: this.businessName,
            phone: this.phoneNumber,
            amount: this.approvedAmount,
            approval: this.status,
            supplierName: this.supplierName,

            merchantApplicationGuid: null,
            applicationGuid: this.applicationGuid,
            step: null,
            age: null,
            navMode: null,
            fullAddress: null,
            middleInitial: null,
            email: null,
            street: null,
            suite: null,
            city: null,
            state: null,
            zip: null,
            income: null,
            birthDate: null,
            vsid: null,
            borrowerApr: this.borrowerApr,
            pmtFactor: null,
            fico: null,
            validInput: false
        };

        return new Application(intf);
    }

    /**
     * Return the application as a string representation, used for filtering/searching
     * 
     * We use a unicode full block as a delimeter, so the only string matches that
     * return are from our fields (unless the user is typing the unicode full block
     * character like a maniac)
     */
    public toString(defs: ColumnDef[]): string {
        let str = "";
        for (let col of defs) {
            if (col.toStringTransform != undefined) {
                str += col.toStringTransform((this as any)[col.field]) + "█";
            } else {
                str += (this as any)[col.field];
            }
        }

        return str.toLowerCase();
    }


    /** Setters */
    set id(id: number) {
        this._id = id;
    }

    set applicationID(applicationID: number) {
        this._applicationID = applicationID;
    }

    set firstName(firstName: string) {
        this._firstName = firstName;
    }

    set lastName(lastName: string) {
        this._lastName = lastName;
    }

    set businessName(businessName: string) {
        this._businessName = businessName;
    }

    set appliedOn(appliedOn: string) {
        this._appliedOn = appliedOn;
    }

    set phoneNumber(phoneNumber: string) {
        this._phoneNumber = phoneNumber;
    }

    set requestedAmount(requestedAmount: number) {
        this._requestedAmount = requestedAmount;
    }

    set salesRep(salesRep: string) {
        this._salesRep = salesRep;
    }

    set approvedAmount(approvedAmount: number) {
        this._approvedAmount = approvedAmount;
    }

    set accountID(accountID: string) {
        this._accountID = accountID;
    }

    set status(status: string) {
        this._status = status;
    }

    set applicationGuid(applicationGuid: string) {
        this._applicationGuid = applicationGuid;
    }

    set borrowerApr(borrowerApr: string) {
        this._borrowerApr = borrowerApr;
    }

    /** Getters */
    get id(): number {
        return this._id;
    }

    get applicationID(): number {
        return this._applicationID;
    }

    get firstName(): string {
        return this._firstName;
    }

    get lastName(): string {
        return this._lastName;
    }

    get businessName(): string {
        return this._businessName;
    }

    get appliedOn(): string {
        return this._appliedOn;
    }

    get phoneNumber(): string {
        return this._phoneNumber;
    }

    get requestedAmount(): number {
        return this._requestedAmount;
    }

    get salesRep(): string {
        return this._salesRep;
    }

    get approvedAmount(): number {
        return this._approvedAmount;
    }

    get accountID(): string {
        return this._accountID;
    }

    get status(): string {
        return this._status;
    }

    get supplierName(): string {
        return this._supplierName;
    }

    get applicationGuid(): string {
        return this._applicationGuid;
    }

    get borrowerApr(): string {
        return this._borrowerApr;
    }

    /** Specialized getters */
    get name(): string {
        return `${this._firstName} ${this._lastName}`;
    }

}
